import { useRef } from "react";

import { NodeDescriptor, DataView } from "@impero-com/common/utils/dataView";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Tag,
    TST_DEFAULT_PAGE_SIZE,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";
import {
    CellContext,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto } from "api/generated/model";
import * as R from "org/risks/types";

import { MapNode } from "../../types";

import { insightsStatusCell } from "./InsightsStatusCell.css";
import { PercentageChange, PercentageChangeProps } from "./PercentageChange";
import { RiskChange, RiskChangeProps } from "./RiskChange";
import { INHERENT_MODE, MapMode, RESIDUAL_MODE } from "./RiskHeatmapTypes";
import { useRiskInsightsTableData } from "./useRiskInsightsTableData";

interface RiskInsightsTableProps {
    startDataView: DataView<NodeDescriptor<MapNode>>;
    startDateRootNode: RiskDirectoryNodeDto | undefined;
    startDateRiskDirectoryView: R.RiskDirectoryView | undefined;
    endDataView: DataView<NodeDescriptor<MapNode>>;
    endDateRootNode: RiskDirectoryNodeDto | undefined;
    endDateRiskDirectoryView: R.RiskDirectoryView | undefined;
    mapMode: MapMode;
    visibleNodeIds: Set<string>;
}

export function RiskInsightsTable({
    startDataView,
    startDateRootNode,
    startDateRiskDirectoryView,
    endDataView,
    endDateRootNode,
    endDateRiskDirectoryView,
    mapMode,
    visibleNodeIds,
}: RiskInsightsTableProps) {
    const { rows, columnNames } = useRiskInsightsTableData({
        startDataView,
        startDateRootNode,
        startDateRiskDirectoryView,
        endDataView,
        endDateRootNode,
        endDateRiskDirectoryView,
        visibleNodeIds,
    });

    const { t } = useTranslation();
    const columns = createColumns(columnNames, mapMode, t);
    const table = useReactTable({
        data: rows,
        columns,
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        getCoreRowModel: getCoreRowModel<RiskInsightsTableRow>(),
        getSortedRowModel: getSortedRowModel<RiskInsightsTableRow>(),
        getPaginationRowModel: getPaginationRowModel<RiskInsightsTableRow>(),
    });

    const tableRef = useRef<HTMLTableElement>(null);

    return (
        <div>
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("RiskDirectory_Insights_NoResults")}
                noSourceRowsFallback={t("RiskDirectory_Insights_NoData")}
            />
            <TSTPagination
                table={table}
                tableElement={tableRef}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}

export type RiskInsightsRowStatus = "new" | "removed" | null;

export interface RiskInsightsTableRow {
    id: string;
    name: string;
    status: RiskInsightsRowStatus;
    process: string;
    entity: string;
    inherentImpact: RiskChangeProps;
    inherentLikelihood: RiskChangeProps;
    residualImpact: RiskChangeProps;
    residualLikelihood: RiskChangeProps;
    coverage: PercentageChangeProps;
}

export interface RiskInsightsColumnNames {
    inherentImpact: string;
    inherentLikelihood: string;
    residualImpact: string;
    residualLikelihood: string;
}

const columnHelper = createColumnHelper<RiskInsightsTableRow>();

function createColumns(
    columnNames: RiskInsightsColumnNames,
    mapMode: MapMode,
    t: TFunction
) {
    const baseColumns = [
        columnHelper.accessor((row) => row.process, {
            id: "process",
            header: () => t("RiskDirectory_Insights_HeaderProcess"),
            cell: (info) => info.getValue(),
            enableSorting: true,
        }),
        columnHelper.accessor((row) => row.name, {
            id: "name",
            header: () => t("RiskDirectory_Insights_HeaderRisk"),
            cell: (info) => <RiskNameCell info={info} t={t} />,
            enableSorting: true,
        }),
        columnHelper.accessor((row) => row.entity, {
            id: "entity",
            header: () => t("RiskDirectory_Insights_HeaderEntity"),
            cell: (info) => info.getValue(),
            enableSorting: true,
        }),
    ];

    const inherentColumns = [
        columnHelper.accessor((row) => row.inherentImpact, {
            id: "inherentImpact",
            header: () => columnNames.inherentImpact,
            cell: (info) => <RiskChange value={info.getValue()} />,
            enableSorting: false,
        }),
        columnHelper.accessor((row) => row.inherentLikelihood, {
            id: "inherentLikelihood",
            header: () => columnNames.inherentLikelihood,
            cell: (info) => <RiskChange value={info.getValue()} />,
            enableSorting: false,
        }),
    ];

    const residualColumns = [
        columnHelper.accessor((row) => row.residualImpact, {
            id: "residualImpact",
            header: () => columnNames.residualImpact,
            cell: (info) => <RiskChange value={info.getValue()} />,
            enableSorting: false,
        }),
        columnHelper.accessor((row) => row.residualLikelihood, {
            id: "residualLikelihood",
            header: () => columnNames.residualLikelihood,
            cell: (info) => <RiskChange value={info.getValue()} />,
            enableSorting: false,
        }),
    ];

    const coverageColumn = columnHelper.accessor((row) => row.coverage, {
        id: "coverage",
        header: () => t("RiskDirectory_Insights_HeaderCoverage"),
        cell: (info) => <PercentageChange value={info.getValue()} />,
        enableSorting: false,
    });

    switch (mapMode) {
        case INHERENT_MODE:
            return [...baseColumns, ...inherentColumns, coverageColumn];
        case RESIDUAL_MODE:
            return [...baseColumns, ...residualColumns, coverageColumn];
        default:
            return assertNever(mapMode);
    }
}

function RiskNameCell({
    info,
    t,
}: {
    info: CellContext<RiskInsightsTableRow, string>;
    t: TFunction;
}) {
    const status = info.row.original.status;
    return (
        <span className={insightsStatusCell}>
            {status === "new" && (
                <Tag variant="success">{t("RiskDirectory_Insights_New")}</Tag>
            )}
            {status === "removed" && (
                <Tag variant="error">{t("RiskDirectory_Insights_Removed")}</Tag>
            )}
            {info.getValue()}
        </span>
    );
}
